<template>
  <div
    v-if="show"
    class="banner-wrapper w-full bg-outtalentBlue text-outtalentLime px-6 py-5 flex items-center justify-between gap-4 overflow-hidden"
  >
    <!-- Flying caps in the background -->
    <span v-for="cap in caps" :key="cap.id" class="flying-cap" :style="cap.style">🎓</span>

    <div class="flex items-center gap-4 min-w-0 relative z-10">
      <div class="min-w-0">
        <p class="text-lg font-bold text-white leading-tight">
          🎉 Congratulations, {{ userStore.currentUser?.first_name }}! 🎓
        </p>
        <p class="text-sm leading-snug mt-0.5">
          You've passed your main stage exams — an incredible achievement!
          <template v-if="showFormLink">
            You are ready to move on to the job application stage.
            <router-link
              :to="APP_ROUTES.GRADUATION_FORM"
              class="underline font-semibold ml-1 hover:opacity-80"
            >
              Fill in the graduation form →
            </router-link>
          </template>
          <template v-else>
            You are ready to graduate. We're so proud of you! 🎓
          </template>
        </p>
      </div>
    </div>
    <button
      @click="dismiss"
      class="shrink-0 text-white/80 hover:text-white transition-colors p-1 rounded relative z-10"
      aria-label="Dismiss"
    >
      <X class="w-4 h-4" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, reactive } from 'vue'
import { useRoute } from 'vue-router'
import { X } from 'lucide-vue-next'
import { useUserStore } from '@/stores/user'
import { useGraduationStore } from '@/stores/graduation'
import { APP_ROUTES } from '@/const/appRoutes'

// Each cap: random starting x position, random horizontal drift, random size, random delay/duration
const caps = reactive(
  Array.from({ length: 18 }, (_, i) => ({
    id: i,
    style: {
      left: `${(i * 5.5 + Math.random() * 4).toFixed(1)}%`,
      fontSize: `${(1.1 + Math.random() * 1.4).toFixed(2)}rem`,
      animationDuration: `${(3.2 + Math.random() * 1.8).toFixed(2)}s`,
      animationDelay: `${(Math.random() * 2.5).toFixed(2)}s`,
      '--drift': `${((Math.random() > 0.5 ? 1 : -1) * (20 + Math.random() * 60)).toFixed(0)}px`,
      '--spin': `${((Math.random() > 0.5 ? 1 : -1) * (200 + Math.random() * 300)).toFixed(0)}deg`,
    },
  })),
)

const userStore = useUserStore()
const graduationStore = useGraduationStore()
const route = useRoute()

const dismissedRef = ref(false)

const storageKey = computed(() =>
  userStore.currentUser ? `graduation_banner_dismissed_${userStore.currentUser.id}` : null,
)

const isDismissed = computed(() => {
  if (dismissedRef.value) return true
  const key = storageKey.value
  return key ? localStorage.getItem(key) === 'true' : false
})

function dismiss() {
  dismissedRef.value = true
  const key = storageKey.value
  if (key) localStorage.setItem(key, 'true')
}

onMounted(async () => {
  if (!graduationStore.status) {
    try { await graduationStore.fetchGraduationStatus() } catch { /* non-critical */ }
  }
})

const graduationStatus = computed(() => graduationStore.status?.outtalent_graduation_status)

const show = computed(() => {
  if (graduationStatus.value !== 'graduating') return false
  if (route.path === '/my-progress' || route.path === '/graduation' || route.path === '/graduation-expedition') return false
  return !isDismissed.value
})

const showFormLink = computed(
  () => !graduationStore.status?.has_interview_preparation_plan,
)
</script>

<style scoped>
.banner-wrapper {
  position: relative;
}

.flying-cap {
  position: absolute;
  bottom: -10%;
  pointer-events: none;
  opacity: 0;
  animation-name: fly-up;
  animation-timing-function: ease-out;
  animation-iteration-count: infinite;
  animation-fill-mode: both;
}

@keyframes fly-up {
  0% {
    transform: translateY(0px) translateX(0px) rotate(0deg);
    /* fast launch, decelerating as it rises */
    animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
    opacity: 0;
  }
  8% {
    opacity: 0.65;
  }
  35% {
    transform: translateY(-100px) translateX(calc(var(--drift, 30px) * 0.5)) rotate(calc(var(--spin, 270deg) * 0.5));
    /* gravity kicks in: accelerates back down */
    animation-timing-function: cubic-bezier(0.55, 0.055, 0.675, 0.19);
    opacity: 0.5;
  }
  88% {
    opacity: 0.25;
  }
  100% {
    transform: translateY(30px) translateX(var(--drift, 30px)) rotate(var(--spin, 270deg));
    opacity: 0;
  }
}
</style>
